// Shared type definitions for the EQUOS Portal
// Extracted from StaffManagement.svelte, BusinessManagement.svelte, SignupFlow.svelte

// Role types matching Flutter app UserTypes
export type Role = 'accountAdmin' | 'businessAdmin' | 'staff' | 'client' | 'freight' | 'account';

// Map portal roles to Flutter userType values
export const ROLE_TO_USERTYPE: Record<Role, string> = {
  'accountAdmin': 'account_admin',
  'businessAdmin': 'business_admin',
  'staff': 'staff_user',
  'client': 'client_user',
  'freight': 'freight_user',
  'account': 'account_user',
};

// Role display labels
export const ROLE_LABELS: Record<Role, string> = {
  'accountAdmin': 'Account Admin',
  'businessAdmin': 'Business Admin',
  'staff': 'Staff',
  'client': 'Client',
  'freight': 'Freight',
  'account': 'Account',
};

// Available modules for permissions
export const AVAILABLE_MODULES = [
  { id: 'dashboard', label: 'Dashboard', icon: '📊' },
  { id: 'inventory', label: 'Inventory', icon: '📦' },
  { id: 'orderManager', label: 'Orders', icon: '📋' },
  { id: 'manufacturing', label: 'Manufacturing', icon: '🏭' },
  { id: 'warehouse', label: 'Warehouse', icon: '🏢' },
  { id: 'transport', label: 'Transport', icon: '🚛' },
  { id: 'contacts', label: 'Contacts', icon: '👥' },
  { id: 'accounts', label: 'Accounts', icon: '💰' },
] as const;

// Default modules by role
export const DEFAULT_MODULES_BY_ROLE: Record<Role, string[]> = {
  'accountAdmin': ['dashboard', 'inventory', 'orderManager', 'manufacturing', 'warehouse', 'transport', 'contacts', 'accounts'],
  'businessAdmin': ['dashboard', 'inventory', 'orderManager', 'manufacturing', 'warehouse', 'transport', 'contacts', 'accounts'],
  'staff': ['dashboard', 'warehouse', 'manufacturing'],
  'client': ['dashboard', 'inventory', 'orderManager', 'warehouse', 'manufacturing', 'transport', 'accounts'],
  'freight': ['dashboard', 'transport'],
  'account': ['dashboard', 'orderManager', 'accounts', 'inventory', 'contacts'],
};

// Business entity type
export type Business = {
  id: string;
  name: string;
  industry?: string | null;
  createdAt: Date | string;
  isActive?: boolean;
  isArchived?: boolean;
};

// Warehouse type with access control fields
export type Warehouse = {
  id: string;
  name: string;
  address?: string;
  contactPerson?: string;
  email?: string;
  phone?: string;
  mobile?: string;
  addressLine1?: string;
  addressLine2?: string;
  suburb?: string;
  postcode?: string;
  state?: string;
  country?: string;
  isActive?: boolean;
  isArchived?: boolean;
  isDisabled?: boolean;
  ownerId?: string;
  allowedUserTypes?: string[];
  allowedUserIds?: string[];
  isOutsourced?: boolean;
  linkedBusinessIds?: string[];
  linkedBusinessNames?: string[];
};

// Staff member type
export type StaffMember = {
  id: string;
  email: string;
  name: string;
  role: Role;
  status: 'active' | 'pending' | 'inactive';
  addedAt: string;
  inviteCode?: string;
};

// Team invitation type
export type Invitation = {
  id: string;
  email: string;
  name: string;
  role: Role;
  userType: string;
  invitedBy: string;
  inviterEmail: string;
  businessId: string | null;
  businessName: string;
  locationId?: string;
  locationName?: string;
  allowedLocationIds?: string[];
  allowedModules?: string[];
  createdAt: string;
  expiresAt: string;
  status: 'pending' | 'accepted' | 'expired' | 'revoked';
};

// Business invitation type (for 3PL client invitations)
export type BusinessInvitation = {
  id: string;
  type: 'business_invitation';
  status: 'pending' | 'accepted' | 'expired' | 'revoked';
  inviteeEmail: string;
  inviteeName: string;
  inviteeCompanyName: string;
  invitedBy: string;
  inviterEmail: string;
  inviterBusinessId: string;
  inviterBusinessName: string;
  linkedWarehouseIds: string[];
  warehouseNames: string[];
  createdAt: string;
  expiresAt: string;
  acceptedAt: string | null;
  createdBusinessId: string | null;
  createdUserId: string | null;
};

// Warehouse form data (used by WarehouseFormModal)
export type WarehouseFormData = {
  name: string;
  contactPerson: string;
  email: string;
  phone: string;
  mobile: string;
  addressLine1: string;
  addressLine2: string;
  suburb: string;
  postcode: string;
  state: string;
  country: string;
  address: string;
};

// User config document structure
export type UserConfig = {
  businesses: Business[];
  archivedBusinesses?: Business[];
  selectedBusinessId: string | null;
  businessId?: string | null; // Default business for custom claims (used by Go backend)
  warehouses: Record<string, Warehouse[]>;
};

// ============================================
// Support Ticket Types
// ============================================

// Ticket priority levels
export type TicketPriority = 'low' | 'normal' | 'high' | 'urgent';

export const TICKET_PRIORITIES: { id: TicketPriority; label: string; color: string }[] = [
  { id: 'low', label: 'Low', color: '#6b7280' },
  { id: 'normal', label: 'Normal', color: '#3b82f6' },
  { id: 'high', label: 'High', color: '#f59e0b' },
  { id: 'urgent', label: 'Urgent', color: '#ef4444' },
];

// Ticket categories
export type TicketCategory = 'general' | 'billing' | 'technical' | 'feature_request';

export const TICKET_CATEGORIES: { id: TicketCategory; label: string }[] = [
  { id: 'general', label: 'General' },
  { id: 'billing', label: 'Billing' },
  { id: 'technical', label: 'Technical' },
  { id: 'feature_request', label: 'Feature Request' },
];

// Ticket status workflow: Open → In Progress → Awaiting Response → Resolved → Closed
export type TicketStatus = 'open' | 'in_progress' | 'awaiting_response' | 'resolved' | 'closed';

export const TICKET_STATUSES: { id: TicketStatus; label: string; color: string }[] = [
  { id: 'open', label: 'Open', color: '#3b82f6' },
  { id: 'in_progress', label: 'In Progress', color: '#8b5cf6' },
  { id: 'awaiting_response', label: 'Awaiting Response', color: '#f59e0b' },
  { id: 'resolved', label: 'Resolved', color: '#22c55e' },
  { id: 'closed', label: 'Closed', color: '#6b7280' },
];

// Support ticket document type
export type Ticket = {
  id: string;
  userId: string;
  userEmail: string;
  userName: string;
  subject: string;
  description: string;
  category: TicketCategory;
  priority: TicketPriority;
  status: TicketStatus;
  emailNotifications: boolean;
  createdAt: Date | string;
  updatedAt: Date | string;
};

// Ticket reply type (subcollection)
export type TicketReply = {
  id: string;
  authorId: string;
  authorName: string;
  authorType: 'user' | 'support';
  message: string;
  createdAt: Date | string;
};

// ============================================
// Feature Request Types
// ============================================

// Feature request status workflow: open → review → planned → completed (or declined)
export type FeatureRequestStatus = 'open' | 'review' | 'planned' | 'completed' | 'declined';

export const FEATURE_REQUEST_STATUSES: { id: FeatureRequestStatus; label: string; color: string }[] = [
  { id: 'open', label: 'Open', color: '#3b82f6' },
  { id: 'review', label: 'Under Review', color: '#8b5cf6' },
  { id: 'planned', label: 'Planned', color: '#f59e0b' },
  { id: 'completed', label: 'Completed', color: '#22c55e' },
  { id: 'declined', label: 'Declined', color: '#6b7280' },
];

// Feature request categories (aligned with app modules)
export type FeatureRequestCategory = 'inventory' | 'orders' | 'warehouse' | 'transport' | 'billing' | 'manufacturing' | 'general' | 'other';

export const FEATURE_REQUEST_CATEGORIES: { id: FeatureRequestCategory; label: string }[] = [
  { id: 'inventory', label: 'Inventory' },
  { id: 'orders', label: 'Orders' },
  { id: 'warehouse', label: 'Warehouse' },
  { id: 'transport', label: 'Transport' },
  { id: 'billing', label: 'Billing' },
  { id: 'manufacturing', label: 'Manufacturing' },
  { id: 'general', label: 'General' },
  { id: 'other', label: 'Other' },
];

// Feature request document type
export type FeatureRequest = {
  id: string;
  title: string;
  description: string;
  category: FeatureRequestCategory;
  status: FeatureRequestStatus;
  authorId: string;
  authorName: string;
  authorEmail: string;
  voteCount: number;
  createdAt: Date | string;
  updatedAt: Date | string;
};

// Feature request reply type (subcollection)
export type FeatureRequestReply = {
  id: string;
  authorId: string;
  authorName: string;
  authorType: 'user' | 'admin';
  message: string;
  createdAt: Date | string;
};

// Feature request vote type (for tracking user votes)
export type FeatureRequestVote = {
  oderId: string;
  vote: 1;  // Only upvotes for v1
  votedAt: Date | string;
};

// ============================================
// Credit Application Types
// ============================================

// Credit application status workflow: pending → approved/rejected
export type CreditApplicationStatus = 'pending' | 'approved' | 'rejected';

export const CREDIT_APPLICATION_STATUSES: { id: CreditApplicationStatus; label: string; color: string }[] = [
  { id: 'pending', label: 'Pending Review', color: '#f59e0b' },
  { id: 'approved', label: 'Approved', color: '#22c55e' },
  { id: 'rejected', label: 'Rejected', color: '#ef4444' },
];

// Australian states for billing address
export const AUSTRALIAN_STATES = [
  { value: 'NSW', label: 'New South Wales' },
  { value: 'VIC', label: 'Victoria' },
  { value: 'QLD', label: 'Queensland' },
  { value: 'SA', label: 'South Australia' },
  { value: 'WA', label: 'Western Australia' },
  { value: 'TAS', label: 'Tasmania' },
  { value: 'NT', label: 'Northern Territory' },
  { value: 'ACT', label: 'Australian Capital Territory' },
] as const;

// Credit application document type
export type CreditApplication = {
  id: string;
  businessId: string;
  userId: string;

  // Business Information
  businessName: string;
  abn: string;
  tradingName?: string;

  // Contact Details
  contactFirstName: string;
  contactLastName: string;
  contactEmail: string;
  contactPhone: string;
  contactPosition?: string;

  // Billing Address
  billingAddressLine1: string;
  billingAddressLine2?: string;
  billingSuburb: string;
  billingState: string;
  billingPostcode: string;
  billingCountry: string;

  // Status
  status: CreditApplicationStatus;
  submittedAt: Date | string;
  reviewedAt?: Date | string;
  reviewedBy?: string;
  rejectionReason?: string;
};

// ============================================
// Integration Types
// ============================================

export type SyncDirection = 'inbound' | 'outbound' | 'bidirectional';

export const SYNC_DIRECTIONS: { id: SyncDirection; label: string }[] = [
  { id: 'inbound', label: 'Inbound (from integration)' },
  { id: 'outbound', label: 'Outbound (to integration)' },
  { id: 'bidirectional', label: 'Bidirectional' },
];

export type IntegrationConfig = {
  syncOrders: boolean;
  syncInventory: boolean;
  orderSyncDirection: SyncDirection;
  inventorySyncDirection: SyncDirection;
};

export type Integration = {
  id: string;
  name: string;
  description: string;
  icon: string;
  enabled: boolean;
};

export type ConnectedIntegration = {
  provider: string;
  nangoConnectionId: string;
  status: 'connected' | 'disconnected' | 'error';
  connectedAt: string;
  connectedBy: string;
  lastSyncAt: string | null;
  lastSyncStatus: string | null;
  config: IntegrationConfig;
  error: string | null;
};

// Firestore: businessdata/{businessId}/apiKeys/{keyId}
export type ApiKeyScope = 'read:orders' | 'write:orders' | 'read:inventory' | 'write:inventory' | 'read:contacts' | 'write:contacts' | 'read:warehouse' | 'write:warehouse';

export const API_KEY_SCOPES: { id: ApiKeyScope; label: string; group: string }[] = [
  { id: 'read:orders', label: 'Read Orders', group: 'Orders' },
  { id: 'write:orders', label: 'Write Orders', group: 'Orders' },
  { id: 'read:inventory', label: 'Read Inventory', group: 'Inventory' },
  { id: 'write:inventory', label: 'Write Inventory', group: 'Inventory' },
  { id: 'read:contacts', label: 'Read Contacts', group: 'Contacts' },
  { id: 'write:contacts', label: 'Write Contacts', group: 'Contacts' },
  { id: 'read:warehouse', label: 'Read Warehouse', group: 'Warehouse' },
  { id: 'write:warehouse', label: 'Write Warehouse', group: 'Warehouse' },
];

export type ApiKey = {
  id: string;
  name: string;
  keyPrefix: string;
  scopes: ApiKeyScope[];
  createdAt: string;
  createdBy: string;
  lastUsedAt: string | null;
  status: 'active' | 'revoked';
};

// Firestore: businessdata/{businessId}/webhooks/{webhookId}
export type WebhookEventId = 'order.created' | 'order.updated' | 'order.completed' | 'inventory.low_stock' | 'inventory.updated' | 'contact.created' | 'shipment.dispatched' | 'shipment.delivered';

export const WEBHOOK_EVENTS: { id: WebhookEventId; label: string; group: string }[] = [
  { id: 'order.created', label: 'Order Created', group: 'Orders' },
  { id: 'order.updated', label: 'Order Updated', group: 'Orders' },
  { id: 'order.completed', label: 'Order Completed', group: 'Orders' },
  { id: 'inventory.low_stock', label: 'Low Stock Alert', group: 'Inventory' },
  { id: 'inventory.updated', label: 'Inventory Updated', group: 'Inventory' },
  { id: 'contact.created', label: 'Contact Created', group: 'Contacts' },
  { id: 'shipment.dispatched', label: 'Shipment Dispatched', group: 'Shipping' },
  { id: 'shipment.delivered', label: 'Shipment Delivered', group: 'Shipping' },
];

export type Webhook = {
  id: string;
  url: string;
  events: WebhookEventId[];
  signingSecret?: string;
  status: 'active' | 'inactive';
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  lastTriggeredAt: string | null;
  lastTriggeredStatus: 'success' | 'failed' | null;
};

// ============================================
// Sync History Types
// ============================================

// Firestore: businessdata/{businessId}/integrations/{provider}/syncHistory/{entryId}
export type SyncHistoryEntry = {
  id: string;
  timestamp: string;
  syncType: 'orders' | 'inventory';
  status: 'success' | 'partial' | 'failed';
  provider: string;
  durationMs: number;
  counts: Record<string, number>;
  errors?: string[];
  details?: Record<string, any>;
};
